import { useEffect, useState } from 'react';
import { AUTH_CONSTANTS } from '../_MODULES/_API';
import { API_CORE } from '../../../../../_CORE/API/ApiCORE';
import { notify } from '../../../../../_CORE/APP/Templates/_UI/Alerts/AppAlertHost';
import { useForm } from '../../../../../_CORE/APP/Templates/_UI/Form/_index';
import { useUserAuthenticated } from '../../../../../_CORE/APP/Hooks/Contexts/_Auth/userAuthenticatedContext';
import { useReactNavigation } from '../../../../../_CORE/APP/Hooks/Hooks/useReactNavigation';
import { STORAGE_SESSION_HELPER } from '../../../../../_CORE/HELPERS/storageSession.helper';
import { APP_STORAGE } from '../../../../../_CORE/_Config/AppConfig.Storage';

const normalize = (response, fallback) => {
    const status = response?.status ?? response?.code_status ?? 500;
    if (status >= 400 || response?.success === false) throw new Error(response?.message || fallback);
    return response?.data ?? response;
};

export const useAuthVerifyCode = () => {
    const { onCallbackCredentialUserAuth } = useUserAuthenticated();
    const { redirectTo } = useReactNavigation();
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [challengeToken, setChallengeToken] = useState(() => STORAGE_SESSION_HELPER.GET_STORAGE(APP_STORAGE.AUTH_2FA_CHALLENGE) || '');
    const [emailMasked, setEmailMasked] = useState(() => STORAGE_SESSION_HELPER.GET_STORAGE(APP_STORAGE.AUTH_2FA_EMAIL_MASKED) || '');
    const [expiresIn, setExpiresIn] = useState(() => STORAGE_SESSION_HELPER.GET_STORAGE(APP_STORAGE.AUTH_2FA_EXPIRES) || '');

    useEffect(() => {
        if (!challengeToken) redirectTo('/ingresar');
    }, [challengeToken, redirectTo]);

    const clearChallenge = () => {
        STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_2FA_CHALLENGE);
        STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_2FA_EMAIL_MASKED);
        STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_2FA_EXPIRES);
    };

    const FORM = useForm({
        initialValues: { code: '' },
        schema: { code: { required: true, minLength: 6, maxLength: 6 } },
        onSubmit: async (values) => {
            const code = String(values.code || '').replace(/\D/g, '').slice(0, 6);
            if (code.length !== 6) {
                notify.warning('Codigo incompleto', 'Ingresa los 6 digitos enviados a tu correo.');
                return;
            }
            setIsLoading(true);
            try {
                const endpoint = API_CORE.ENDPOINTS[AUTH_CONSTANTS.VERIFY_TWO_FACTOR];
                const response = await API_CORE.SERVICES.POST_RESOURCE(endpoint, { challenge_token: challengeToken, code });
                const data = normalize(response, 'No se pudo verificar el codigo.');
                if (!data?.accessToken) throw new Error('El servidor no devolvio el token de acceso.');
                clearChallenge();
                notify.success('Acceso verificado', 'La verificacion en dos pasos fue correcta.');
                await onCallbackCredentialUserAuth({ accessToken: data.accessToken });
            } catch (error) {
                notify.error('Codigo no valido', error?.message || 'No se pudo verificar el codigo.');
            } finally {
                setIsLoading(false);
            }
        },
    });

    const resend = async () => {
        if (!challengeToken || isResending) return;
        setIsResending(true);
        try {
            const endpoint = API_CORE.ENDPOINTS[AUTH_CONSTANTS.RESEND_TWO_FACTOR];
            const response = await API_CORE.SERVICES.POST_RESOURCE(endpoint, { challenge_token: challengeToken });
            const data = normalize(response, 'No se pudo reenviar el codigo.');
            const nextToken = data?.challengeToken || '';
            setChallengeToken(nextToken);
            setEmailMasked(data?.emailMasked || emailMasked);
            setExpiresIn(data?.expiresIn || expiresIn);
            STORAGE_SESSION_HELPER.SET_SESSION_STORAGE(APP_STORAGE.AUTH_2FA_CHALLENGE, nextToken);
            STORAGE_SESSION_HELPER.SET_SESSION_STORAGE(APP_STORAGE.AUTH_2FA_EMAIL_MASKED, data?.emailMasked || emailMasked);
            STORAGE_SESSION_HELPER.SET_SESSION_STORAGE(APP_STORAGE.AUTH_2FA_EXPIRES, data?.expiresIn || expiresIn);
            notify.info('Nuevo codigo enviado', `Revisa ${data?.emailMasked || emailMasked || 'tu correo'}.`);
        } catch (error) {
            notify.error('No se pudo reenviar', error?.message || 'Intenta iniciar sesion nuevamente.');
        } finally {
            setIsResending(false);
        }
    };

    const backToLogin = () => {
        clearChallenge();
        redirectTo('/ingresar');
    };

    return { FORM, isLoading, isResending, emailMasked, expiresIn, resend, backToLogin };
};
